import { useCallback, useRef, useState } from 'react';
import { getFileCatalyst } from '../../api/EvidenceSourceUpload';
import { useToast } from '../../contexts/toastContext/ToastContext';
import { t } from 'i18next';

export interface UseFileCatalystReturn {
  downloadFileCatalyst: () => Promise<void>;
  isDownloading: boolean;
  cancelDownload: () => void;
  progress: number
}

export const useFileCatalystDownload = (): UseFileCatalystReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const { showToast } = useToast();
  const [progress, setProgress] = useState(0);
  const controllerRef =
    useRef<AbortController | null>(null);

  const cancelDownload = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
  };

  const downloadFileCatalyst = useCallback(async () => {
    try {
      setIsDownloading(true);
      const controller = new AbortController();
      controllerRef.current = controller;
      const response = await getFileCatalyst(
        controller.signal,
        (progressEvent) => {
          const total = progressEvent.total || 0;

          if (total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / total
            );

            setProgress(percentCompleted);
          }
        }
      );
      const blob = response.data;
      // Extract filename from header
      const disposition =
        response.headers['content-disposition'];

      let filename = 'FileCatalyst.exe';

      if (
        disposition &&
        disposition.includes('filename=')
      ) {
        filename = disposition
          .split('filename=')[1]
          .split(';')[0]
          .replace(/"/g, '')
          .trim();

      }
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setIsDownloading(false);
      const status = e?.response?.status;

      if (
        status === 500 &&
        e?.response?.data instanceof Blob
      ) {
        const text = await e.response.data.text();

        let errorMessage = text;

        try {
          const json = JSON.parse(text);

          errorMessage = json.message || text;
        } catch {
          errorMessage = t('fileCatalyst.highSpeedUploads.errorWhileProcessing')
        }

        showToast(
          errorMessage,
          'error',
          undefined,
          'center'
        );
      } else {
        showToast(
          t(
            'fileCatalyst.highSpeedUploads.errorWhileProcessing'
          ),
          'error',
          undefined,
          'center'
        );
      }

    } finally {
      setIsDownloading(false);
      controllerRef.current = null;
    }
  }, []);

  return {
    downloadFileCatalyst,
    cancelDownload,
    isDownloading,
    progress
  };
};